<template>
  <div>
    <div class="flex flex-end flex-vertical-center">
      <button-link
        v-if="showRefresh"
        @click="doRefresh"
        color="is-secondary"
        class="mr-100 ml-50"
        is-short
      >Refresh
      </button-link>

      <div
        v-if="showPresets"
        class="presets"
      >
        <p class="presets--prefix">Preset</p>

        <b-select
          size="is-small"
          class="presets--select"
          expanded
          v-model="preset"
          @input="changePreset"
        >
          <option :value="null">None</option>
          <option
            v-for="preset in presets"
            :key="preset.id"
            :value="preset"
          >{{ preset.label }}</option>
        </b-select>

        <icon-default
          class="presets--button"
          icon="menu"
          @click="doManagePresets"
        />
      </div>

      <div
        v-if="showActions"
        class="presets ml-50"
      >
        <p class="presets--prefix">Action</p>

        <b-select
          size="is-small"
          class="presets--select"
          expanded
          v-model="action"
        >
          <option :value="null">None</option>
          <option
            v-for="action in actions"
            :key="action.id"
            :value="action"
          >{{ action.label }}</option>
        </b-select>

        <icon-default
          class="presets--button"
          icon="play"
          @click="runAction"
        />
      </div>

      <b-dropdown v-if="showDownloadButton">
        <template #trigger>
          <b-button
            label="Download"
            class="ml-50 is-short"
            outlined
            size="is-small"
            type="is-secondary"
            icon-left="download"
            :loading="isDownloading"
          />
        </template>

        <b-dropdown-item @click="doDownload(true)">Download selected {{ downloadLabel }}</b-dropdown-item>
        <b-dropdown-item @click="doDownload">Download all {{ downloadLabel }}</b-dropdown-item>
      </b-dropdown>

      <button-outlined
        v-if="showColumnsButton"
        @click="isColumnsSidebarOpen = true"
        icon-left="table-column"
        color="is-secondary"
        class="ml-50"
        is-short
      >Columns
      </button-outlined>

      <div
        v-if="showFiltersButton"
        class="ml-50"
      >
        <button-default
          v-if="filterCount > 0"
          icon-left="filter"
          @click="isFiltersSidebarOpen = true"
          is-short
        >Filters ({{ filterCount }})</button-default>

        <button-outlined
          v-else
          icon-left="filter"
          @click="isFiltersSidebarOpen = true"
          is-short
        >Filters</button-outlined>
      </div>
    </div>

    <!--Columns Sidebar-->
    <b-sidebar
      type="is-white"
      fullheight
      :overlay="false"
      right
      v-model="isColumnsSidebarOpen"
    >
      <div class="sidebar_main">
        <div class="sidebar_main--header">
          <h4>{{ columnsHeading }}</h4>
        </div>

        <div class="sidebar_main--content">
          <slot name="columns"/>
        </div>

        <div class="sidebar_main--footer">
          <div class="flex">
            <button-outlined
              class="mr-100"
              color="is-secondary"
              @click="isColumnsSidebarOpen = false"
            >Close</button-outlined>

            <button-link @click="doColumnsReset">reset</button-link>
          </div>

          <button-outlined @click="applyColumns">Apply Columns</button-outlined>
        </div>
      </div>
    </b-sidebar>

    <!--Filters Sidebar-->
    <b-sidebar
      type="is-white"
      fullheight
      :overlay="false"
      right
      v-model="isFiltersSidebarOpen"
    >
      <div class="sidebar_main">
        <div class="sidebar_main--header">
          <h4>{{ filtersHeading }}</h4>
        </div>

        <div class="sidebar_main--content">
          <slot name="filters"/>
        </div>

        <div class="sidebar_main--footer">
          <div class="flex">
            <button-outlined
              class="mr-100"
              color="is-secondary"
              v-if="!hideApplyFilterButton"
              @click="isFiltersSidebarOpen = false"
            >Close</button-outlined>

            <button-link @click="doFiltersReset">reset</button-link>
          </div>

          <button-outlined v-if="!hideApplyFilterButton" @click="applyFilters">Apply Filters</button-outlined>
          <button-outlined
            v-else
            color="is-secondary"
            @click="isFiltersSidebarOpen = false"
          >Close</button-outlined>
        </div>
      </div>
    </b-sidebar>
  </div>
</template>

<script>
export default {
  name: "TableButtons",
  props: {
    presets: {
      type: Array,
      default: () => []
    },
    defaultPreset: {
      type: Object|null,
      default: null
    },
    actions: {
      type: Array,
      default: () => []
    },
    showRefresh: {
      type: Boolean,
      default: false,
    },
    showPresets: {
      type: Boolean,
      default: false,
    },
    showActions: {
      type: Boolean,
      default: false,
    },
    showDownloadButton: {
      type: Boolean,
      default: false,
    },
    showColumnsButton: {
      type: Boolean,
      default: false,
    },
    showFiltersButton: {
      type: Boolean,
      default: false,
    },
    hideApplyFilterButton: {
      type: Boolean,
      default: false,
    },
    columnsHeading: {
      type: String,
      default: 'Columns'
    },
    filtersHeading: {
      type: String,
      default: 'Filters'
    },
    filterCount: {
      type: Number,
      default: 0,
    },
    doColumnsReset: {
      type: Function,
      default: () => {
      }
    },
    doColumnsApply: {
      type: Function,
      default: () => {
      }
    },
    doFiltersReset: {
      type: Function,
      default: () => {
      }
    },
    doFiltersApply: {
      type: Function,
      default: () => {
      }
    },
    doRefresh: {
      type: Function,
      default: () => {
      }
    },
    downloadLabel: {
      type: String,
      default: ''
    },
    doDownload: {
      type: Function,
      default: () => {
      }
    },
    isDownloading: {
      type: Boolean,
      default: false
    },
    doManagePresets: {
      type: Function,
      default: () => {
      }
    },
  },
  data() {
    return {
      preset: this.defaultPreset,
      action: null,
      isColumnsSidebarOpen: false,
      isFiltersSidebarOpen: false,
    };
  },
  methods: {
    changePreset() {
      this.$emit('presetChanged', this.preset)
    },
    runAction() {
      this.$emit('runAction', this.action)
    },
    applyFilters() {
      this.isFiltersSidebarOpen = false;
      this.doFiltersApply()
    },
    applyColumns() {
      this.isColumnsSidebarOpen = false;
      this.doColumnsApply()
    }
  },
};
</script>

<style scoped lang="scss">
@import "../../styles/variables/variables";

.sidebar_main {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  &--header {
    padding: 10px;
  }

  &--content {
    flex-grow: 1;
    overflow-y: scroll;
    padding: 10px 0;
    border-top: 1px solid $border;
    border-bottom: 1px solid $border;
  }

  &--footer {
    padding: 8px 15px;
    display: flex;
    justify-content: space-between;
  }
}

.presets {
  position: relative;
  display: flex;
  align-items: center;
  border: 1px solid $border;
  padding-left: 6px;

  &--prefix {
    position: absolute;
    top: 50%;
    left: 4px;
    z-index: 20;
    margin-top: -0.3125rem;
    font-size: 0.625rem;
    color: $text-muted;
  }

  :deep &--select {

    .select {
      height: 2em;

      &:after {
        margin-top: -0.525em!important;
        margin-right: -0.5em!important;
      }

      select {
        height: 2em;
        padding-top: 0!important;
        padding-bottom: 0!important;
        padding-left: 40px!important;
        border-top: none!important;
        border-bottom: none!important;
        border-left: none!important;

        &:focus {
          border-color: $border;
          box-shadow: none;
        }
      }
    }
  }

  &--button {
    padding: 0 2px;
    cursor: pointer;

    &:hover {
     color: $link-hover;
    }
  }
}

</style>
